class CollectionBanner extends HTMLElement {
  constructor() {
    super();
    this.stage = null;
    this.strip = null;
    this.bgSlots = [];
    this.frameCanvases = [];
    this.timeline = null;
    this.isInitialized = false;
    this.lastWidth = 0;
    this._resizeTid = null;
    this._gsapPolls = 0;
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.waitForGsap(), { timeout: 50 });
    } else {
      requestAnimationFrame(() => this.waitForGsap());
    }
  }

  disconnectedCallback() {
    this.destroy();
  }

  waitForGsap() {
    if (window.gsap && window.ScrollTrigger) {
      this.init();
      return;
    }
    if (this._gsapPolls++ > 50) return;
    setTimeout(() => this.waitForGsap(), 50);
  }

  init() {
    if (this.isInitialized) return;
    this.stage = this.querySelector('.collection-banner__stage');
    this.strip = this.querySelector('.collection-banner__content-strip');
    this.bgSlots = Array.from(this.querySelectorAll('.collection-banner__bg-slot'));
    this.frameCanvases = Array.from(this.querySelectorAll('.collection-banner__frame-canvas'));
    if (!this.stage || this.bgSlots.length === 0) return;

    this.lockViewportHeight();
    this.drawAllFrames();
    this.lastWidth = window.innerWidth;
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('orientationchange', () => {
      this.lastWidth = -1;
      this.onResize();
    });

    if (this.bgSlots.length === 1) {
      this.isInitialized = true;
      return;
    }

    const rawDwell = parseFloat(this.dataset.dwellRatio);
    const dwellRatio = Number.isFinite(rawDwell)
      ? Math.min(Math.max(rawDwell, 0), 100) / 100
      : 0.5;

    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    this.buildTimeline(dwellRatio);
    this.decodeImages();
    this.setupHeaderHide();

    this.isInitialized = true;
  }

  /* Hide the site header (header-v2) while any pixel of this section is in
     the viewport. Multiple collection-banner instances cooperate via a shared
     Set so the body class stays correct even if more than one is on screen. */
  setupHeaderHide() {
    if (!('IntersectionObserver' in window)) return;
    window.__cbInView = window.__cbInView || new Set();
    const host = this.closest('.collection-banner__stage') || this;
    this.viewportObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.__cbInView.add(this);
        } else {
          window.__cbInView.delete(this);
        }
        document.body.classList.toggle(
          'collection-banner-in-view',
          window.__cbInView.size > 0
        );
      },
      { threshold: 0, rootMargin: '0px 0px -90% 0px' }
    );
    this.viewportObserver.observe(host);
  }

  decodeImages() {
    const imgs = this.querySelectorAll('.collection-banner__bg-slot img');
    if (imgs.length === 0) return;
    Promise.all(
      Array.from(imgs).map((img) => {
        if (img.complete && img.decode) return img.decode().catch(() => {});
        return new Promise((resolve) => {
          img.addEventListener(
            'load',
            () => {
              (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(resolve);
            },
            { once: true }
          );
          img.addEventListener('error', resolve, { once: true });
        });
      })
    ).then(() => {
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    });
  }

  /* Lock CSS heights to the JS-time window.innerHeight so the stage,
     each slide, and each bg slot all share one truth — prevents the
     iOS URL-bar mismatch that exposes a thin black line at the bottom
     of the stage on the final slide. */
  lockViewportHeight() {
    this.style.setProperty('--cb-vh', `${window.innerHeight + 2}px`);
  }

  /* ── Frame canvas ──────────────────────────────────────
     Thickness + corner radius scale per breakpoint so the inner concave
     radius reads correctly on small screens. */
  frameSpec() {
    const w = window.innerWidth;
    if (w >= 768) return { thick: 20, R: 12 };
    return { thick: 12, R: 8 };
  }

  drawFrame(canvas) {
    if (!canvas) return;
    const W = canvas.offsetWidth || window.innerWidth;
    const H = canvas.offsetHeight || window.innerHeight;
    if (W === 0 || H === 0) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const { thick, R } = this.frameSpec();
    const ix = thick;
    const iy = thick;
    const iw = W - thick * 2;
    const ih = H - thick * 2;
    const r = Math.max(0, Math.min(R, iw / 2, ih / 2));

    const outer = new Path2D();
    outer.rect(0, 0, W, H);

    const inner = new Path2D();
    inner.moveTo(ix + r, iy);
    inner.lineTo(ix + iw - r, iy);
    inner.arcTo(ix + iw, iy, ix + iw, iy + r, r);
    inner.lineTo(ix + iw, iy + ih - r);
    inner.arcTo(ix + iw, iy + ih, ix + iw - r, iy + ih, r);
    inner.lineTo(ix + r, iy + ih);
    inner.arcTo(ix, iy + ih, ix, iy + ih - r, r);
    inner.lineTo(ix, iy + r);
    inner.arcTo(ix, iy, ix + r, iy, r);
    inner.closePath();

    const combined = new Path2D();
    combined.addPath(outer);
    combined.addPath(inner);
    ctx.fillStyle = '#ffffff';
    ctx.fill(combined, 'evenodd');
  }

  drawAllFrames() {
    this.frameCanvases.forEach((c) => this.drawFrame(c));
  }

  /* ── GSAP timeline ─────────────────────────────────── */
  buildTimeline(dwellRatio) {
    const { gsap } = window;
    const steps = this.bgSlots.length - 1;
    const stepMultiplier = 1 + dwellRatio;
    const vh = () => window.innerHeight;

    /* First bg sits at 0, the rest start fully below.
       force3D: true keeps the elements on a permanent GPU layer on Safari/iOS
       (the "auto" default drops back to 2D mid-scroll, which Safari shows as jitter). */
    gsap.set(this.bgSlots, { yPercent: (i) => (i === 0 ? 0 : 100), force3D: true });
    if (this.strip) gsap.set(this.strip, { y: 0, force3D: true });

    this.timeline = gsap.timeline({
      defaults: { ease: 'none', force3D: true },
      scrollTrigger: {
        trigger: this.stage,
        pin: this.stage,
        pinSpacing: true,
        anticipatePin: 1,
        fastScrollEnd: true,
        start: 'top top',
        end: () => '+=' + vh() * stepMultiplier * steps,
        scrub: true,
        invalidateOnRefresh: true,
        refreshPriority: 1,
      },
    });

    /* Strip — ONE continuous tween across the full timeline, so there's no
       sub-frame seam at slide boundaries. */
    if (this.strip) {
      this.timeline.to(
        this.strip,
        { y: () => -vh() * steps, duration: steps },
        0
      );
    }

    /* Each non-first bg slot slides up over its own step. */
    for (let i = 1; i < this.bgSlots.length; i++) {
      this.timeline.to(
        this.bgSlots[i],
        { yPercent: 0, duration: 1 },
        i - 1
      );
    }
  }

  /* Width-only resize handler — ignores iOS URL-bar height jitter that fights the scrub. */
  onResize() {
    if (window.innerWidth === this.lastWidth) return;
    this.lastWidth = window.innerWidth;
    clearTimeout(this._resizeTid);
    this._resizeTid = setTimeout(() => {
      this.lockViewportHeight();
      this.drawAllFrames();
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }, 150);
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this._resizeTid);
    if (this.viewportObserver) {
      this.viewportObserver.disconnect();
      this.viewportObserver = null;
    }
    if (window.__cbInView) {
      window.__cbInView.delete(this);
      document.body.classList.toggle(
        'collection-banner-in-view',
        window.__cbInView.size > 0
      );
    }
    if (this.timeline) {
      if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
      this.timeline.kill();
      this.timeline = null;
    }
  }
}

if (!customElements.get('collection-banner')) {
  customElements.define('collection-banner', CollectionBanner);
}
